import React, { useEffect, useMemo, useState } from "react";

import Button from "@cayuse/shared-react.components.button";
import IconButton from "@cayuse/shared-react.components.icon-button";
import CustomDropdown from "@cayuse/shared-react.custom-dropdown";
import PropTypes from "prop-types";

import { useRuleBuilderContext } from "containers/Rules/context/useRuleBuilderContext";
import { ACTION_TYPES, ROLE_SP_PROPOSAL_REVIEWER, RULE_TYPES } from "globals/constants";
import { useDictionary, useGroupRoles } from "hooks";

import CertificationAction from "./CertificationAction";
import IntegrationAction from "./IntegrationAction";
import NotificationAction from "./NotificationAction";
import ReviewAction from "./ReviewAction";
import styles from "./styles.css";
import { compileConditions, getActionOptions, mapRuleTypeToActionOptions } from "./utils";

const componentHash = {
	[ACTION_TYPES.REVIEW]: ReviewAction,
	[ACTION_TYPES.NOTIFICATION]: NotificationAction,
	[ACTION_TYPES.INTEGRATION]: IntegrationAction,
	[ACTION_TYPES.CERTIFICATION]: CertificationAction,
};

const ActionBuilder = ({ rule, isNew, handleRuleSave, handleRuleCancel }) => {
	const [ actionType, setActionType ] = useState();
	const { getGroupRoleByName } = useGroupRoles();
	const dictionary = useDictionary("ruleBuilder");
	const {
		onRuleChange,
		setEditingAction,
		setFormChanged,
		editingCondition,
		isEditingAction,
	} = useRuleBuilderContext();

	const { ruleType } = rule;
	const editMode = !!rule.condition && !rule.isConditionEditing;
	const ActionComponent = actionType in componentHash ? componentHash[actionType] : undefined;

	const { showRTActions, showUnitReviewAction } = useMemo(() => {
		if (rule.condition && rule.condition.operands) {
			return compileConditions(rule.condition.operands);
		}

		return {};
	}, [ rule.condition?.operands, rule.isConditionEditing ]);

	const saveButtonEnabled = useMemo(
		() => [
			RULE_TYPES.RT_CERTIFICATION,
			RULE_TYPES.DISCLOSURE,
			RULE_TYPES.RT_REVIEW,
			RULE_TYPES.UNIT_REVIEW,
			RULE_TYPES.AD_HOC_REVIEW,
			RULE_TYPES.NOTIFICATION,
			RULE_TYPES.AD_HOC_NOTIFICATION,
		].includes(ruleType)
	);

	useEffect(() => {
		const type = mapRuleTypeToActionOptions(ruleType);
		setActionType(type);
	}, [ rule.isConditionEditing, rule.isActionEditing ]);

	const handleChange = (event, { value }) => {
		//TODO: Rework this!!!!
		if (
			[
				RULE_TYPES.UNIT_REVIEW,
				RULE_TYPES.DISCLOSURE,
				RULE_TYPES.RT_REVIEW,
			].includes(value)
		) {
			const spProposalReviewerRole = getGroupRoleByName(
				ROLE_SP_PROPOSAL_REVIEWER
			);
			rule.targetId = spProposalReviewerRole.id;
		}

		onRuleChange({
			...rule,
			ruleType: value === ACTION_TYPES.CERTIFICATION ? RULE_TYPES.RT_CERTIFICATION : null
		});

		setActionType(value);
		setFormChanged(true);
	};

	const handleActionChanged = rule => {
		setFormChanged(true);
		onRuleChange(rule);
	};

	const handleEdit = () => {
		rule.isActionEditing = true;
		setEditingAction(true);
	};

	return (
		<div data-component="Rules\List\ActionBuilder">
			{editMode && (
				<div className={styles.actionContainer}>
					<div>
						{rule.isActionEditing && (
							<div className={styles.actionDropdownWrapper} role="application">
								<label>{dictionary.action ?? "Action"}</label>
								<CustomDropdown
									customClassname={styles.actionDropdown}
									label={`Select ${dictionary.action ?? "Action"}`}
									options={getActionOptions(dictionary, showRTActions)}
									value={actionType ?? ""}
									onChange={handleChange}
									isCustomized={true}
									isCustomStyling={true}
									isCustomOnChange={true}
								/>
							</div>
						)}
					</div>
					<div className={styles.actionTypeWrapper}>
						<div className={styles.actionTypeContent}>
							{ActionComponent && (
								<ActionComponent
									dictionary={dictionary}
									isNew={rule.isAddingMode || rule.isActionEditing}
									showRTActions={showRTActions}
									showUnitReviewAction={showUnitReviewAction}
									rule={rule}
									onActionChange={handleActionChanged}
								/>
							)}
						</div>
						{!isNew && !rule.isActionEditing && (
							<IconButton
								secondary
								labelPosition="left"
								icon="pencil"
								onClick={handleEdit}
								disabled={editingCondition || isEditingAction}
								className={styles.editConditionButton}
							>
								{dictionary.conditionBuilder.editAction || "Edit Action"}
							</IconButton>
						)}
					</div>
					{rule.isActionEditing && (
						<div className={styles.saveButtonGroup}>
							<Button
								type="button"
								secondary
								onClick={handleRuleCancel}
							>
								{dictionary.cancelNewRule || "Cancel"}
							</Button>
							<Button
								type="button"
								disabled={!saveButtonEnabled}
								onClick={handleRuleSave}
							>
								{dictionary.saveNewRule || "Save"}
							</Button>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

ActionBuilder.propTypes = {
	isNew: PropTypes.bool,
	rule: PropTypes.shape({
		type: PropTypes.string,
	}).isRequired,
};

export default ActionBuilder;
